import React, { useContext, useMemo } from "react"
import {
    HorizontalStack,
    useTheme,
    type StyleProps,
} from "@mallardbay/lib-react-components"

import PrimaryGradientButton from "~components/shared/primary-gradient-button"
import ResponsiveBlock from "~components/shared/responsive-block"
import { formatPrice } from "~utils/helpers"
import { ResponsiveContext } from "~config/responsive-provider"
import type {
    ListingFieldsFragment,
    PublicListingFieldsFragment,
} from "~graphql/generated/graphql"
import { usePricingPackagesByListingQuery } from "~graphql/generated/graphql"
import Box from "~components/shared/todo-lib-react-components/box"
import { Text } from "~components/shared/todo-lib-react-components/text"
import { useDesktopMediaQueries } from "~components/shared/todo-lib-react-components/hooks/use-media-queries"

interface Props {
    readonly listing: ListingFieldsFragment | PublicListingFieldsFragment
    readonly setDrawerIsOpen: (value: boolean) => void
}

export default function DetailsBoxMobile({ listing, setDrawerIsOpen }: Props) {
    const { windowScroll } = useContext(ResponsiveContext)
    const styles = useStyles({ isScrolledToBottom: windowScroll.isBottom })

    const { data, loading } = usePricingPackagesByListingQuery({
        variables: {
            listingId: listing.id,
        },
    })

    const pricingPackages = useMemo(
        () =>
            [
                ...(data?.pricingPackagesByListing || []).filter(
                    (pricingPackage) => pricingPackage.is_active
                ),
            ].sort(
                (pricingPackageA, pricingPackageB) =>
                    pricingPackageA.price - pricingPackageB.price
            ),
        [data]
    )
    const lowestPrice = formatPrice(listing.lowest_pricing_package?.price)

    const { isLargerThanDesktop } = useDesktopMediaQueries()
    if (isLargerThanDesktop) return null

    return (
        <Box style={styles.root}>
            <ResponsiveBlock>
                <Box style={styles.container}>
                    <Box>
                        <Box style={styles.unit}>
                            {pricingPackages.length} packages available
                        </Box>
                        <HorizontalStack style={styles.amount}>
                            <Text style={styles.unit}>from</Text>
                            <Text>{lowestPrice}</Text>
                        </HorizontalStack>
                    </Box>
                    <Box>
                        <PrimaryGradientButton
                            fullwidth
                            isLoading={loading}
                            onClick={() => setDrawerIsOpen(true)}
                        >
                            View packages
                        </PrimaryGradientButton>
                    </Box>
                </Box>
            </ResponsiveBlock>
        </Box>
    )
}

function useStyles({ isScrolledToBottom }: { isScrolledToBottom?: boolean }) {
    const { colors, shadows } = useTheme()

    const root: StyleProps = {
        transform: isScrolledToBottom ? "translateY(80px)" : "",
        transition: "0.25s ease-out",
        position: "fixed",
        width: "100%",
        bottom: 0,
        left: 0,
        color: colors.label,
        backgroundColor: colors.bgColor,
        // TODO fix shadow
        boxShadow: shadows.base,
    }

    const container: StyleProps = {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    }

    const unit: StyleProps = {
        fontSize: "14px",
        fontWeight: 300,
        marginBottom: "2px",
        marginRight: "-6px",
        color: colors.label,
    }

    const amount: StyleProps = {
        fontSize: "21px",
        fontWeight: 700,
        alignItems: "flex-end",
    }

    return {
        root,
        container,
        amount,
        unit,
    }
}
